import React, { useMemo, useCallback } from 'react';
import cn from 'classnames';
import styles from './index.module.scss';

import { UiKit } from 'components';
import { findCurrency } from 'utils/DICTIONARY';

/**
 * @param {React.InputHTMLAttributes<HTMLInputElement> & {
 *  label?: string
 *  caption?: string
 *  min?: number
 *  size?: import('utils/types').Size
 *  value?: string
 *  onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void
 *  currency?: string
 *  allowDecimal?: boolean
 *  disabled?: boolean
 *  placeholder?: string
 *  error?: string
 *  className?: string
 *  inputRef?: React.RefObject<HTMLInputElement>
 * }}
 */

export default function PriceInput({
  label = '',
  caption = '',
  min = 0,
  size = 'md',
  value = '',
  onChange = () => {},
  currency = 'USD',
  allowDecimal = true,
  disabled = false,
  placeholder = 'say, $2',
  error = '',
  className = '',
  inputRef = null,
  ...inputHtmlProps
}) {
  const currencySymbol = useMemo(() => findCurrency(currency), [currency]);

  const onInputChange = useCallback(
    (value, event) => {
      const regex = allowDecimal ? /^\d*([.])?\d*$/ : /^\d*$/;
      const isValid = regex.test(value);
      if (!isValid) return;
      onChange(value, event);
    },
    [onChange, allowDecimal]
  );

  const onInputBlur = useCallback(
    event => {
      const value = event.target.value;
      const formattedValue = value ? `${+value}` : '';
      event.target.value = formattedValue;
      onChange(formattedValue, event);
    },
    [onChange]
  );

  return (
    <UiKit.Input
      inputRef={inputRef}
      type="number"
      size={size}
      label={label}
      value={value}
      onBlur={onInputBlur}
      onChange={onInputChange}
      min={min}
      step={allowDecimal ? '0.1' : '1'}
      error={error}
      disabled={disabled}
      placeholder={placeholder}
      caption={caption}
      className={className}
      inputWrapperClassName={styles.inputContainer}
      inputClassName={cn(styles.input, { [styles.paddLeft]: currencySymbol })}
      {...inputHtmlProps}
    >
      <span
        className={cn(styles.currency, {
          [styles.disabled]: disabled,
          [styles.invalid]: error
        })}
      >
        {currencySymbol}
      </span>
    </UiKit.Input>
  );
}
